import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import { PageHero, Reveal, SectionHeader, CTABanner } from "@/components/ui-blocks";
import { SERVICES } from "@/data/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const svc = SERVICES.find((s) => s.slug === slug);
  if (!svc) return <Navigate to="/services" replace />;
  const nextSvc = SERVICES[(SERVICES.findIndex((s) => s.slug === slug) + 1) % SERVICES.length];

  return (
    <div data-testid={`service-detail-${slug}`}>
      <section className="relative pt-40 pb-16 lg:pt-48 lg:pb-24 overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-4 mb-6 overline">
            <Link to="/services" className="text-ink-400 hover:text-accent">Services</Link>
            <span className="text-ink-600">/</span>
            <span className="text-accent">{svc.number}</span>
          </div>
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-9">
              <h1 className="font-display text-6xl md:text-8xl lg:text-[10rem] leading-[0.85] uppercase tracking-tight">
                {svc.title}
              </h1>
            </div>
            <div className="lg:col-span-3">
              <p className="text-ink-400 text-base leading-relaxed">{svc.short}</p>
            </div>
          </div>
        </div>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-16">
          <div className="aspect-[21/9] overflow-hidden border border-white/10">
            <img src={svc.image} alt={svc.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="relative py-24 lg:py-32">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionHeader eyebrow="Scope" title="What's included." />
          </div>
          <div className="lg:col-span-7 lg:col-start-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {svc.scope.map((s, i) => (
              <Reveal key={s} delay={i * 0.08}>
                <div className="flex items-start gap-4 border border-white/10 p-6 hover:border-accent transition-colors duration-500">
                  <Check size={18} className="text-accent shrink-0 mt-1" />
                  <div>
                    <span className="overline block">{String(i + 1).padStart(2, "0")}</span>
                    <p className="font-heading text-lg font-bold mt-1">{s}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 lg:py-32 bg-obsidian-900/40 border-y border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <SectionHeader eyebrow="Process" title={<>How we work{"\n"}on this scope.</>} />
          </div>
          <div className="lg:col-span-6 space-y-8">
            {[
              { n: "01", title: "Discovery", text: "We meet on site, review your goals, and align on scope, budget, and constraints before we draft a single line." },
              { n: "02", title: "Estimate & Schedule", text: "You get a detailed cost breakdown and CPM schedule — no wishful thinking, no hidden buffers." },
              { n: "03", title: "Execution", text: "A senior PM and superintendent lead the job daily. You get weekly reports and one number to call." },
              { n: "04", title: "Delivery", text: "We finish the punch list before we finish the invoice. You get warranty walk-throughs and a partner for the next project." },
            ].map((step) => (
              <Reveal key={step.n}>
                <div className="flex items-start gap-6 border-l border-white/10 pl-6 hover:border-accent transition-colors duration-500">
                  <span className="font-display text-3xl text-accent leading-none">{step.n}</span>
                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase">{step.title}</h3>
                    <p className="text-ink-400 text-sm mt-2 leading-relaxed">{step.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Next service */}
      <section className="relative border-y border-white/5 bg-obsidian-950">
        <Link
          to={`/services/${nextSvc.slug}`}
          data-testid="next-service"
          className="group block max-w-[1600px] mx-auto px-6 lg:px-12 py-16 lg:py-24 flex items-center justify-between gap-8"
        >
          <div>
            <p className="overline mb-4">Next Service — {nextSvc.number}</p>
            <h3 className="font-display text-4xl md:text-6xl lg:text-7xl uppercase leading-none group-hover:translate-x-3 transition-transform duration-500">
              {nextSvc.title}
            </h3>
          </div>
          <ArrowUpRight size={40} className="text-ink-600 group-hover:text-accent transition-colors" />
        </Link>
      </section>

      <CTABanner eyebrow="Get a Quote" heading={<>Ready to price{"\n"}your project?</>} />
    </div>
  );
}
